// Mind2Magic CRM — Shared Itinerary Helpers
// Pure functions used by both docx-generator.js and pdf-finalizer.js
// Extracted to avoid adding exports to docx-generator.js (which breaks Vite's docx bundling)

export function extractFamilyName(lead) {
  const titular = lead.titular || ''
  const parts = titular.trim().split(/\s+/)
  return parts.length > 1 ? parts.slice(-1)[0] : titular
}

export function getFirstHotel(lead) {
  if (lead.hotels?.length) return lead.hotels[0].name || 'el hotel'
  if (lead.formData?.hotelEspecifico) return lead.formData.hotelEspecifico
  return 'el hotel'
}

export function getTravelStartDate(lead) {
  if (lead.parkEntries?.length) {
    const dates = lead.parkEntries.map(pe => pe.validFrom || pe.startDate).filter(Boolean)
    if (dates.length) return dates.sort()[0]
  }
  if (lead.travelDate) return lead.travelDate
  if (lead.hotels?.length) {
    const dates = lead.hotels.map(h => h.checkIn).filter(Boolean)
    if (dates.length) return dates.sort()[0]
  }
  return null
}

export function detectParks(lead) {
  const parks = []
  const type = lead.type || ''
  const dest = (lead.destination || '').toLowerCase()
  const fd = lead.formData || {}

  if (lead.parkEntries?.length) {
    for (const pe of lead.parkEntries) {
      const park = (pe.park || '').toLowerCase()
      if (park.includes('magic kingdom') || park.includes('mk')) parks.push('mk')
      if (park.includes('hollywood') || park.includes('hs')) parks.push('hs')
      if (park.includes('epcot') || park.includes('ep')) parks.push('ep')
      if (park.includes('animal kingdom') || park.includes('ak')) parks.push('ak')
      if (park.includes('universal studios') || park.includes('uso')) parks.push('uso')
      if (park.includes('islands of adventure') || park.includes('ioa')) parks.push('ioa')
      if (park.includes('epic universe') || park.includes('epic')) parks.push('epic')
    }
  }

  if (fd.selectedParks?.length) {
    for (const p of fd.selectedParks) {
      if (!parks.includes(p)) parks.push(p)
    }
  }

  const gr = fd.guideResponses || {}
  if (gr.selectedParks_disney?.length) {
    for (const p of gr.selectedParks_disney) {
      if (!parks.includes(p)) parks.push(p)
    }
  }
  if (gr.selectedParks_universal?.length) {
    for (const p of gr.selectedParks_universal) {
      if (!parks.includes(p)) parks.push(p)
    }
  }

  if (!parks.length) {
    if (type.includes('disney') || dest.includes('disney')) {
      parks.push('mk', 'hs', 'ep', 'ak')
    }
    if (type.includes('universal') || dest.includes('universal')) {
      parks.push('uso', 'ioa', 'epic')
    }
  }

  return [...new Set(parks)]
}

export function detectDiningPlan(lead) {
  if (lead.parkEntries?.some(pe => pe.diningPlan)) return true
  if (lead.formData?.diningPlan) return true
  return false
}

export function detectMealPlanType(lead) {
  const dp = lead.parkEntries?.find(pe => pe.diningPlan)?.diningPlan || lead.formData?.diningPlan || ''
  if (['quick_service', 'standard'].includes(dp)) return dp
  if (dp === 'deluxe') return 'standard'
  if (dp === true) return 'standard'
  const lower = String(dp).toLowerCase()
  if (lower.includes('deluxe') || lower.includes('standard') || lower.includes('table')) return 'standard'
  return 'quick_service'
}

export function detectNoCoasters(gr) {
  if (!gr.visionViaje) return false
  const v = gr.visionViaje.toLowerCase()
  return v.includes('no montaña') || v.includes('no montañas') ||
    v.includes('sin montaña') || v.includes('sin montañas') ||
    v.includes('no subimos a montaña') || v.includes('no juegos extremos') ||
    v.includes('no extremos') || v.includes('no rusas') ||
    v.includes('sin rusas') || v.includes('no roller') ||
    v.includes('no coaster')
}

export function isClosedAttraction(rideName, parkClosures) {
  if (!parkClosures?.length) return false
  const rideNorm = rideName.toLowerCase().replace(/[^a-z0-9]/g, '')
  return parkClosures.some(c => {
    const closeNorm = c.name.toLowerCase().replace(/[^a-z0-9]/g, '')
    return rideNorm.includes(closeNorm) || closeNorm.includes(rideNorm)
  })
}

export function getParkName(code) {
  const names = {
    mk: 'Magic Kingdom',
    hs: 'Hollywood Studios',
    ep: 'EPCOT',
    ak: 'Animal Kingdom',
    uso: 'Universal Studios Orlando',
    ioa: 'Islands of Adventure',
    epic: 'Epic Universe',
  }
  return names[code] || code
}

export function getEnhancedHours(day, parkCode, hotelInfo, parkHoursData) {
  const result = { display: day.hours || '', earlyEntry: null, extendedHours: null }

  if (day.hours) {
    if (day.hours.includes('Early Entry')) result.earlyEntry = true
    if (day.hours.includes('Extended Evening') || day.hours.includes('Extended')) result.extendedHours = true
    result.display = day.hours
    return result
  }

  const dateKey = day.date || ''
  const apiHours = parkHoursData?.parkHours?.[dateKey]?.[parkCode]
  if (!apiHours) return result

  const open = apiHours.open || '9:00'
  const close = apiHours.close || '21:00'
  const ee = apiHours.early_entry
  const ext = apiHours.extended_hours

  let display = `${open} – ${close}`

  const isDPark = ['mk', 'hs', 'ep', 'ak'].includes(parkCode)
  if (isDPark && hotelInfo.isDisneyGuest && ee) {
    display = `${ee} (Early Entry) – ${close}`
    result.earlyEntry = ee
  }
  if (isDPark && hotelInfo.disneyTier === 'deluxe' && ext) {
    const openPart = result.earlyEntry ? `${ee} (Early Entry)` : open
    display = `${openPart} – ${ext} (Extended Evening)`
    result.extendedHours = ext
  }

  result.display = display
  return result
}

export function formatDateShort(dateInput) {
  try {
    const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
    if (isNaN(d.getTime())) return ''
    const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`
  } catch {
    return ''
  }
}

export function buildDefaultSchedule(lead, parks) {
  const SARA_ORDER = ['hs', 'ak', 'ep', 'mk', 'uso', 'ioa', 'epic']
  const sorted = [...parks].sort((a, b) => {
    const ai = SARA_ORDER.indexOf(a)
    const bi = SARA_ORDER.indexOf(b)
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })

  const startDate = lead.travelDate ? new Date(lead.travelDate + 'T12:00:00') : null
  return sorted.map((parkCode, i) => {
    let dateLabel = ''
    let date = ''
    if (startDate) {
      const d = new Date(startDate)
      d.setDate(d.getDate() + i + 1) // +1 to skip check-in day
      dateLabel = formatDateShort(d)
      date = d.toISOString().split('T')[0]
    }
    return { parkCode, dateLabel, date, hours: '' }
  })
}

export function isNighttimeShow(show) {
  const name = show.name.toLowerCase()
  return name.includes('happily ever after') || name.includes('fantasmic') ||
    name.includes('luminous') || show.times?.includes('9:00 PM') ||
    show.times?.includes('8:00') || show.times?.includes('10:00')
}

export function filterRides(allRides, selectedRides, hasGuideData, ritmo, ifTimePermits = []) {
  const norm = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, '').trim()

  const matchesSelected = (ride, selectedNorm) => {
    const rideNorm = norm(ride.name)
    return selectedNorm.some(sel =>
      rideNorm.includes(sel) || sel.includes(rideNorm) ||
      (ride.key && sel.includes(norm(ride.key.replace(/_/g, ' '))))
    )
  }

  if (hasGuideData && selectedRides.length) {
    const selectedNorm = selectedRides.map(norm)
    for (const ride of allRides) ride._selected = matchesSelected(ride, selectedNorm)
    for (const ride of ifTimePermits) ride._selected = matchesSelected(ride, selectedNorm)

    const boosted = ifTimePermits.filter(r => r._selected)
    if (boosted.length > 0) {
      const tailSelected = allRides.length > 10
        ? allRides.slice(10).filter(r => r._selected)
        : []

      const saraTop = allRides.slice(0, 10)
      const saraRest = allRides.slice(10).filter(r => !r._selected)
      const boostPool = [...boosted, ...tailSelected]

      const combined = [...saraTop, ...boostPool]
      const maxPrimary = Math.min(15, combined.length)
      const primary = combined.slice(0, maxPrimary)
      const secondary = [...combined.slice(maxPrimary), ...saraRest]
      const boostedNames = new Set(boosted.map(r => norm(r.name)))
      const remainingIfTime = ifTimePermits.filter(r => !boostedNames.has(norm(r.name)))

      if (ritmo === 'relajado') {
        const cutoff = Math.ceil(primary.length * 0.6)
        return { primary: primary.slice(0, cutoff), secondary: [...primary.slice(cutoff), ...secondary], skipped: [], ifTimePermitsFiltered: remainingIfTime }
      }
      return { primary, secondary, skipped: [], ifTimePermitsFiltered: remainingIfTime }
    }
  }

  if (ritmo === 'relajado') {
    const cutoff = Math.ceil(allRides.length * 0.6)
    return { primary: allRides.slice(0, cutoff), secondary: allRides.slice(cutoff), skipped: [], ifTimePermitsFiltered: ifTimePermits }
  }

  return { primary: allRides, secondary: [], skipped: [], ifTimePermitsFiltered: ifTimePermits }
}

export function filterShows(allShows, selectedShows, hasGuideData, finalDia) {
  if (!hasGuideData || !selectedShows.length) {
    if (finalDia === 'hotel') {
      return allShows.filter(s => !isNighttimeShow(s))
    }
    return allShows
  }

  const norm = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, '').trim()
  const selectedNorm = selectedShows.map(norm)

  const filtered = allShows.filter(show => {
    const showNorm = norm(show.name)
    const isSelected = selectedNorm.some(sel => showNorm.includes(sel) || sel.includes(showNorm))
    if (finalDia === 'shows' && isNighttimeShow(show)) return true
    if (finalDia === 'hotel' && isNighttimeShow(show)) return false
    return isSelected || !hasGuideData
  })

  return filtered
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
